import { useEffect, useRef, useState } from "react";
import {
	deleteTemplatePlan,
	exportTemplateCreate,
	exportTemplateShow,
	getTemplatePlan,
} from "../services/plans-template.service";
import useNotification from "./notification.hook";
import { getOrganizations } from "../services/organizations.service";
import { apiClient } from "../services/config";
import { getAllOrganizationsList, getAllOrganizationTypesList } from "../helper/data.helper";

const UseTemplatePlanActionsHook = () => {
	const { openNotification, helperErrorNotification } = useNotification();

	const timerDownload = useRef(null);

	const [isLoading, setIsLoading] = useState({
		downloadTemplate: false,
		activeOrganization: false,
		activeOrganizationCounter: false,
		organizationTypes: false,
		templatePlan: false,
		deletePlan: false,
		getContentPreset: false,
		createContentPreset: false,
		deletePreset: false,
		copyPreset: false,
		listContentPresets: false,
		contentPresetOrganizationTypes: false,
	});

	const [isModalOpen, setIsModalOpen] = useState({
		downloadMassive: false,
		emailMassive: false,
		crud: false,
		delete: false,
		duplicate: false,
		statusConfiguration: false,
		presetConfiguration: false,
		fieldsConfiguration: false,
		crudContentPreset: false,
		deletePreset: false,
		copyPreset: false,
		applyPreset: false,
	});

	/**
	 * Update loading state
	 * @param loader
	 */
	const updateLoading = (loader) => {
		setIsLoading((l) => ({ ...l, ...loader }));
	};

	/**
	 * Update modal state
	 * @param modal
	 */
	const updateModalState = (modal) => {
		setIsModalOpen((m) => ({ ...m, ...modal }));
	};

	/**
	 * Download plan difference
	 * @param url
	 */
	async function downloadDifferenceHandler(url, filename) {
		try {
			const res = await apiClient.get(url, { responseType: "blob" });
			const fileURL = window.URL.createObjectURL(new Blob([res.data]));
			const fileLink = document.createElement("a");
			fileLink.href = fileURL;
			fileLink.download = filename;
			fileLink.click();
		} catch (error) {
			helperErrorNotification(error);
		}
	}

	/**
	 * Create Download
	 */
	const createDownloadTemplateHandler = (plan) => {
		if (!isLoading.downloadTemplate) {
			updateLoading({ downloadTemplate: true });

			const body = {
				idTemplatePlan: plan.id,
			};

			exportTemplateCreate(
				body,
				(res) => {
					getDownloadTemplate(plan, res.data.id);
				},
				(error) => {
					helperErrorNotification(error);
					updateLoading({ downloadTemplate: false });
				}
			);
		}
	};

	/**
	 * Get Download
	 */
	const getDownloadTemplate = (plan, id) => {
		exportTemplateShow(
			{ idTemplatePlan: plan.id, id },
			(res) => {
				if (res.data.status === "pending") {
					timerDownload.current = setTimeout(() => {
						getDownloadTemplate(plan, id);
					}, 2000);
				} else {
					const link = document.body.appendChild(document.createElement("a"));
					link.href = res.data.url;
					link.target = "_blank";
					link.download = " ";
					link.click();
					document.body.removeChild(link);
					updateLoading({ downloadTemplate: false });
					openNotification({
						text: "Documento generato con successo!",
						status: "success",
					});
				}
			},
			(error) => {
				helperErrorNotification(error);
				updateLoading({ downloadTemplate: false });
			}
		);
	};

	/**
	 * Get List organizations
	 */
	const getAllOrganizationsHandler = async (plan) => {
		updateLoading({ activeOrganization: true });

		const body = {
			template_plan_id: plan.id,
			include: ["organizationType", "reseller", "organizationPlans"],
			filter: { template_plan_active: true },
			per_page: 200,
		};

		const organizations = await getAllOrganizationsList(body);

		updateLoading({ activeOrganization: false });

		return organizations;
	};

	/**
	 * Get List organizations
	 */
	const getActiveOrganizationsCounterHandler = async (planId) => {
		updateLoading({ activeOrganizationCounter: true });
		let counter = 0;

		const body = {
			template_plan_id: planId,
			filter: { template_plan_active: true },
			per_page: 1,
		};

		await getOrganizations({ ...body }, async (res) => {
			counter = res?.meta?.total ?? 0;
		});

		updateLoading({ activeOrganizationCounter: false });

		return counter;
	};

	/**
	 * Get Organization Types
	 */
	const getAllOrganizationTypesHandler = async () => {
		updateLoading({ organizationTypes: true });
		const types = await getAllOrganizationTypesList();
		updateLoading({ organizationTypes: false });
		return types;
	};

	/**
	 * Get template plan handler
	 * @param id
	 * @param filters
	 * @returns {Promise<*|null>}
	 */
	const getTemplatePlanHandler = async (id, filters) => {
		updateLoading({ templatePlan: true });
		try {
			const res = await getTemplatePlan({
				id,
				...filters,
			});
			updateLoading({ templatePlan: false });
			return res.data;
		} catch (error) {
			helperErrorNotification(error);
			updateLoading({ templatePlan: false });
			return null;
		}
	};

	/**
	 * Delete handler
	 */
	const deletePlanHandler = (id, onDelete = () => {}, onError = () => {}) => {
		deleteTemplatePlan(
			id,
			() => {
				openNotification({
					text: "Piano eliminato con successo!",
					status: "success",
				});
				onDelete();
			},
			(err) => {
				helperErrorNotification(err);
				onError();
			}
		);
	};

	useEffect(() => {
		return () => {
			clearTimeout(timerDownload.current);
		};
	}, []);

	return {
		createDownloadTemplateHandler,
		isModalOpen,
		isLoading,
		updateModalState,
		updateLoading,
		getAllOrganizationTypesHandler,
		getAllOrganizationsHandler,
		getTemplatePlanHandler,
		downloadDifferenceHandler,
		deletePlanHandler,
		getActiveOrganizationsCounterHandler,
	};
};

export default UseTemplatePlanActionsHook;
